import { useMutation } from '@tanstack/react-query';
import { apiClient, isNetworkError } from '@/lib/api';
import { useConversationStore } from '@/stores/conversationStore';
import toast from '@/lib/toast';
import type { ChatResponse } from '@/lib/types';

interface ChatMessageRequest {
  conversationId: string;
  message: string;
}

export function useChatMessage() {
  return useMutation({
    mutationFn: ({ conversationId, message }: ChatMessageRequest) => {
      // Get current state from store (at mutation time, not hook creation time)
      const state = useConversationStore.getState();

      // Add user message to history immediately
      state.addChatMessage({
        role: 'user',
        content: message,
        timestamp: new Date(),
      });

      // Get conversation context from store
      if (!state.currentConversation) {
        throw new Error('No conversation context available');
      }

      return apiClient.chat(conversationId, message, {
        conversation: state.currentConversation,
        sentiment: state.sentiment || undefined,
        currentDraft: state.draft || undefined,
        articles: state.draft?.articles || undefined,
      });
    },
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error instanceof Error && (error.message.includes('401') || error.message.includes('403'))) {
        return false;
      }
      // Retry up to 2 times for network errors and 5xx errors
      if (isNetworkError(error) || (error instanceof Error && error.message.includes('500'))) {
        return failureCount < 2;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    onSuccess: (data: ChatResponse) => {
      // Add AI response to history
      useConversationStore.getState().addChatMessage({
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
      });
    },
    onError: (error: Error) => {
      console.error('Chat message failed:', error);
      toast.error('Failed to send message', {
        description: error.message || 'Please try again.',
      });
    },
  });
}

// For fetching chat history from API (if needed)
export function useChatHistory() {
  // This would be used if we persist chat history on the backend
  // For now, we're managing it in local state only
  // Future: Add API endpoint to fetch/persist chat history
  // Future signature: export function useChatHistory(conversationId: string)
  return { chatHistory: useConversationStore((state) => state.chatHistory) };
}
